import { Rect, Svg, Tspan, Text as SvgText } from '@svgdotjs/svg.js';
import { Utils } from 'app/core/utils';
import { StageConstants } from 'core/constants';
import { TextConstants } from './text.constants';
import { defaultTextEffects, extractChunksFromLine, FontBearingDescription,
         FontDescription, isBoldFontWeight, isItalicFontStyle, LeadingFactor,
         normalizedFontFamily, normalizedTextChunk, scaledBearing, Text,
         TextAlignment, TextArea, TextChunk, TextChunkKind, TextColor, TextSize,
         TextEffects
       } from './text.model';

export function applyModel(
  wrapper: Svg, model: Text, font: FontDescription): Svg
{
  const txt = wrapper.find('text').at(0) as SvgText;

  applyChunksToNode(txt, model.text);
  applyFont(txt, font);
  applyFontSize(txt, model.fontSize);
  applyColor(txt, model.color);
  applyBgColor(txt, model.bgColor);
  applyTextEffects(txt, model.effects);
  applyLeading(txt, model.leading);

  applyHorizontalAlignment(
    wrapper, txt, model.horizontalAlignment, scaledBearing(font, model));

  setDyWithModelFontSize(txt, model.leading, model.fontSize);

  applyVerticalAlignment(
    wrapper, txt, model.verticalAlignment, model.fontSize, model.leading);

  return wrapper;
}

export function extractModel(wrapper: Svg): Text
{
  const txt = wrapper.find('text').at(0) as SvgText;

  return {
    text: extractChunksFromNode(txt),
    horizontalAlignment: extractHorizontalAlignment(txt),
    verticalAlignment: extractVerticalAlignment(txt),
    effects: extractTextEffects(txt),
    fontSize: extractFontSize(txt),
    fontName: extractFontFamily(txt),
    fontBold: extractFontBold(txt),
    fontItalic: extractFontItalic(txt),
    color: extractFontColor(txt),
    bgColor: extractBgColor(txt),
    leading: extractLeading(txt),
    availableArea: extractAvailableArea(wrapper)
  };
}

interface AlignmentAttributeMappingPair {
  attribute: string;
  alignment: TextAlignment;
}

interface AlignmentAttributeMapping {
  attributeName: string;
  values: Array<AlignmentAttributeMappingPair>;
}

const VERTICAL_ALIGNMENT_MAPPING: AlignmentAttributeMapping = {
  attributeName: StageConstants.ATTRIBUTE_VERTICAL_ALIGNMENT,
  values: [
    { attribute: 'start', alignment: TextAlignment.Start },
    { attribute: 'middle', alignment: TextAlignment.Middle },
    { attribute: 'end', alignment: TextAlignment.End },
  ]
};

const HORIZONTAL_ALIGNMENT_MAPPING: AlignmentAttributeMapping = {
  attributeName: 'text-anchor',
  values: [
    { attribute: 'start', alignment: TextAlignment.Start },
    { attribute: 'middle', alignment: TextAlignment.Middle },
    { attribute: 'end', alignment: TextAlignment.End },
  ]
};

const DEFAULT_ALIGNMENT = TextAlignment.Start;
const DEFAULT_FONT_FAMILY = 'Open Sans';
const DEFAULT_FONT_SIZE = '16';
const DEFAULT_FONT_COLOR = '#000000';
const DEFAULT_BG_COLOR = 'transparent';
const DEFAULT_LEADING = 1.3;
const DEFAULT_FONT_WEIGHT = 'normal';
const DEFAULT_FONT_STYLE = 'normal';
const DEFAULT_TEXT_DECORATION = 'none';
const DEFAULT_TEXT_TRANSFORM = 'none';

const CLASS_NAME_TEXT_BACKGROUND = 'text-background';

function lookupAlignmentByValue(
  mapping: AlignmentAttributeMapping, attribute: string): TextAlignment
{
  const entry = Utils.find(
    mapping.values,
    (el: AlignmentAttributeMappingPair) => el.attribute === attribute);

  return entry ? entry.alignment : DEFAULT_ALIGNMENT;
}

function lookupAlignment(
  mapping: AlignmentAttributeMapping, node: SvgText): TextAlignment
{
  return lookupAlignmentByValue(mapping, node.attr(mapping.attributeName));
}

function lookupAttributeValue(
  mapping: AlignmentAttributeMapping, alignment: TextAlignment): string
{
  return Utils.find(
    mapping.values,
    (el: AlignmentAttributeMappingPair) => el.alignment === alignment
  ).attribute;
}

function readStyleFromFirstSpan(
  node: SvgText, styleAttribute: string, defaultValue: string): string
{
  const tspans = node.children();
  const firstSpan = tspans.at(0) as Tspan;
  const value = firstSpan
    ? firstSpan.node.style.getPropertyValue(styleAttribute)
    : undefined;

  return (value !== undefined && value !== '') ? value : defaultValue;
}

function getFirstLineOffset(node: SvgText): TextSize
{
  const tspans = node.children();
  const firstSpan = tspans.at(0) as Tspan;
  return parseInt(firstSpan ? firstSpan.attr('dy') : undefined, 10);
}

function extractAvailableArea(wrapper: Svg): TextArea
{
  return {
    width: wrapper.width() as number,
    height: wrapper.height() as number
  };
}

function extractLeading(node: SvgText): LeadingFactor
{
  const fontSize = extractFontSize(node);
  const dy = getFirstLineOffset(node);

  return dy ? (dy / fontSize) : DEFAULT_LEADING;
}

function extractFontColor(node: SvgText): TextColor
{
  return readStyleFromFirstSpan(node, 'fill', DEFAULT_FONT_COLOR);
}

function extractFontFamily(node: SvgText): string
{
  return normalizedFontFamily(
    readStyleFromFirstSpan(node, 'font-family', DEFAULT_FONT_FAMILY));
}

function extractFontSize(node: SvgText): TextSize
{
  return parseInt(
    readStyleFromFirstSpan(node, 'font-size', DEFAULT_FONT_SIZE), 10);
}

function extractHorizontalAlignment(node: SvgText): TextAlignment
{
  return lookupAlignment(HORIZONTAL_ALIGNMENT_MAPPING, node);
}

function extractVerticalAlignment(node: SvgText): TextAlignment
{
  return lookupAlignment(VERTICAL_ALIGNMENT_MAPPING, node);
}

function readEffectStyle(
  node: SvgText, styleAttribute: string, defaultValue: string): string
{
  const firstSpan = node.children().at(0) as Tspan;

  const candidates = [
    readStyleFromFirstSpan(node, styleAttribute, ''),
    firstSpan ? firstSpan.node.getAttribute(styleAttribute) : '',
    node.node.getAttribute(styleAttribute),
  ];

  return candidates.find(value => !!value) || defaultValue;
}

function extractFontBold(node: SvgText): boolean
{
  return isBoldFontWeight(
    readEffectStyle(node, 'font-weight', DEFAULT_FONT_WEIGHT));
}

function extractFontItalic(node: SvgText): boolean
{
  return isItalicFontStyle(
    readEffectStyle(node, 'font-style', DEFAULT_FONT_STYLE));
}

function extractTextEffects(node: SvgText): TextEffects
{
  const decoration =
    readEffectStyle(node, 'text-decoration', DEFAULT_TEXT_DECORATION);
  const transform =
    readEffectStyle(node, 'text-transform', DEFAULT_TEXT_TRANSFORM);

  return {
    underline: decoration.indexOf('underline') !== -1,
    strikethrough: decoration.indexOf('line-through') !== -1,
    allcaps: transform.trim() === 'uppercase',
  };
}

export function extractChunksFromNode(node: SvgText): Array<TextChunk>
{
  const lines = (<any>node.children())
    .map((m: Tspan) => m.text())
    .map(extractChunksFromLine);

  const newline = {
    kind: TextChunkKind.Whitespace,
    breaksLine: true,
    content: '',
  };

  return Utils
    .flattenArray(Utils.intersperse(lines, [newline]))
    .map(normalizedTextChunk);
}

function applyChunksToNode(node: SvgText, chunks: Array<TextChunk>): void
{
  const renderWord = (word: TextChunk) => word.content;
  const renderWhitespace = (space: TextChunk) => space.breaksLine ? '\n' : ' ';

  const renderChunk = (chunk: TextChunk): string => {
    switch (chunk.kind) {
      case TextChunkKind.Word: return renderWord(chunk) || '';
      case TextChunkKind.Whitespace: return renderWhitespace(chunk);
      case TextChunkKind.Control: return '';
      default: return ''; // not reached
    }
  };

  node.node.setAttributeNS(
      'http://www.w3.org/XML/1998/namespace', 'xml:space', 'preserve');
  node.text(chunks.map(renderChunk).join(''));
}

function applyHorizontalAlignment(
  wrapper: Svg,
  node: SvgText,
  alignment: TextAlignment,
  bearing: FontBearingDescription): void
{
  node.attr(HORIZONTAL_ALIGNMENT_MAPPING.attributeName,
            lookupAttributeValue(HORIZONTAL_ALIGNMENT_MAPPING, alignment));

  const width = wrapper.width();

  const svgAlignment = (() => {
    switch (alignment) {
      case TextAlignment.Start: return bearing.left;
      case TextAlignment.Middle: return (width as number) / 2;
      case TextAlignment.End: return (width as number) - bearing.right;
      default: return 0; // not reached
    }
  })();

  node.transform({ translateX: 0});
  node.attr('x', svgAlignment);
}

function firstBaselineOffset(
  node: SvgText, fontSize: TextSize, lineHeight: TextSize): TextSize
{
  const lines = (<any>node.children()) as Array<Tspan>;
  const lineWithContent = lines.findIndex(
      (line: Tspan) => line.node.textContent !== '');
  const index = lineWithContent === -1 ? 0 : lineWithContent;
  const renderedDy = lines[index] ? (Number(lines[index].attr('dy')) || 0) : 0;
  const ascent = fontSize;

  return index * lineHeight + ascent - renderedDy;
}

function applyVerticalAlignment(
  wrapper: Svg,
  node: SvgText,
  alignment: TextAlignment,
  fontSize: TextSize,
  leading: LeadingFactor): void
{
  node.attr(VERTICAL_ALIGNMENT_MAPPING.attributeName,
            lookupAttributeValue(VERTICAL_ALIGNMENT_MAPPING, alignment));

  const height = wrapper.height() as number;
  const lineHeight = fontSize * leading;
  const textHeight = (<any>node.children()).length * lineHeight;
  const emptySpace = height - textHeight;

  const svgAlignment = (() => {
    switch (alignment) {
      case TextAlignment.Start: return {
        offset: 0,
      };
      case TextAlignment.Middle: return {
        offset: (emptySpace / 2),
      };
      case TextAlignment.End: return {
        offset: emptySpace,
      };
      default: return { // not reached
        offset: 0,
      };
    }
  })();

  node.attr(
    'y',
    svgAlignment.offset + firstBaselineOffset(node, fontSize, lineHeight));
}

function applyStyleToLines(node: SvgText, style: string, value: string): void
{
  for (const line of (<any>node.children())) {
    line.css(style, value);
  }
}

function applyFontSize(node: SvgText, size: TextSize): void
{
  applyStyleToLines(node, 'font-size', `${size}px`);
  node.font({'size': size});
}

function applyFont(node: SvgText, font: FontDescription): void
{
  const family = normalizedFontFamily(font.name);

  applyStyleToLines(node, 'font-family', family);
  node.font({'family': family});

  applyStyleToLines(
    node, 'font-weight', font.bold ? 'bold' : DEFAULT_FONT_WEIGHT);
  applyStyleToLines(
    node, 'font-style', font.italic ? 'italic' : DEFAULT_FONT_STYLE);
}

function applyColor(node: SvgText, color: TextColor): void
{
  applyStyleToLines(node, 'fill', color);
}

function textDecorationValue(effects: TextEffects): string
{
  const decorations = [];

  if (effects.underline) {
    decorations.push('underline');
  }

  if (effects.strikethrough) {
    decorations.push('line-through');
  }

  return decorations.length > 0
    ? decorations.join(' ')
    : DEFAULT_TEXT_DECORATION;
}

function applyTextEffects(node: SvgText, effects: TextEffects): void
{
  const applied = effects || defaultTextEffects();

  applyStyleToLines(
    node, 'text-decoration', textDecorationValue(applied));
  applyStyleToLines(
    node, 'text-transform', applied.allcaps ? 'uppercase'
                                            : DEFAULT_TEXT_TRANSFORM);
}

function findBackgroundRect(node: SvgText): Rect | undefined
{
  const wrapper = node.parent() as Svg;
  const rect = wrapper
    ? wrapper.findOne(`rect.${CLASS_NAME_TEXT_BACKGROUND}`)
    : undefined;

  return rect ? (rect as Rect) : undefined;
}

function createBackgroundRect(node: SvgText): Rect
{
  const rect = new Rect().addClass(CLASS_NAME_TEXT_BACKGROUND).attr({
    rx: TextConstants.BACKGROUND_CORNER_RADIUS,
    ry: TextConstants.BACKGROUND_CORNER_RADIUS,
  });

  node.before(rect);

  return rect;
}

function extractBgColor(node: SvgText): TextColor
{
  const rect = findBackgroundRect(node);

  if (! rect) {
    return DEFAULT_BG_COLOR;
  }

  const value = rect.node.style.getPropertyValue('fill')
    || rect.node.getAttribute('fill');

  return (value !== undefined && value !== '') ? value : DEFAULT_BG_COLOR;
}

function applyBgColor(node: SvgText, color: TextColor): void
{
  const rect = findBackgroundRect(node);

  if (color === DEFAULT_BG_COLOR && rect) {
    rect.remove();
    return;
  }

  const wrapper = node.parent() as Svg;
  wrapper.css('overflow', 'visible');

  (rect || createBackgroundRect(node)).attr({
    x: -TextConstants.BACKGROUND_PADDING_X,
    y: -TextConstants.BACKGROUND_PADDING_Y,
    width: (wrapper.width() as number) + 2 * TextConstants.BACKGROUND_PADDING_X,
    height:
        (wrapper.height() as number) + 2 * TextConstants.BACKGROUND_PADDING_Y,
    fill: color,
  });
}

function applyLeading(node: SvgText, leading: LeadingFactor): void
{
  node.leading(leading);
}

function setDyWithModelFontSize(
    node: SvgText, leading: LeadingFactor, fontSize: TextSize): void
{
  const dy = leading * fontSize;
  const lines = (<any>node.children()) as Array<Tspan>;
  let previousLineWithContent = 0;
  lines.forEach((line, index) => {
    line.attr('dy', dy * (index - previousLineWithContent));
    if (line.node.textContent !== '') {
      previousLineWithContent = index;
    }
  });
}
